import { useEffect, useMemo, useState } from 'react'
import { api, DashboardStats } from '../api/client'
import GlobalRunHistory from './GlobalRunHistory'
import TimeRangePicker, { TimeRange, toWindow, windowKey } from '../components/TimeRangePicker'
import { fmtDateTime } from '../utils/datetime'

/**
 * EVERY key added in round 17 is optional on `DashboardStats`, so this page must survive
 * being served by a backend that predates them. The rule followed throughout: a missing
 * number renders as an em dash, never as 0 and never as "NaN" — a dashboard that invents
 * a zero is worse than one that admits it does not know.
 *
 * Second rule, the project's own: a rate always prints the population it was computed
 * over. A bare "97%" is unactionable when the denominator might be 3.
 */

const DASH = '—'

function fmtInt(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : DASH
}

function fmtPercent(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${value}%` : DASH
}

function fmtSeconds(value: number | null | undefined, digits = 0): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(digits)}s` : DASH
}

/** Denominator line for a rate tile. Says so plainly when the count is not available. */
function overSettled(numerator: number | null | undefined, denominator: number | null | undefined): string {
  if (typeof denominator !== 'number' || !Number.isFinite(denominator)) {
    return 'denominator not reported by this backend'
  }
  if (denominator === 0) return 'no settled runs in window'
  return `${fmtInt(numerator)} of ${fmtInt(denominator)} settled runs`
}

/**
 * Coverage sub-line for the SENT page counter, or `null` for "print nothing".
 *
 * `sent_page_count` is only written for a run whose job document the platform could
 * paginate, so this tile's number can be a fraction of the transmissions it appears to
 * describe — it read 2 over a window holding 8. The "counted since" caption that used to
 * hint at the gap was removed at the user's request, which left a knowingly partial
 * counter rendering as complete history. This puts the partiality back, but as the
 * measurement it actually is rather than a deployment date.
 *
 * Printed ONLY when the two counts disagree. When every transmitted run contributed a
 * page count the number is exactly what it looks like, and a line confirming that is the
 * redundant sub-text the user asked to be rid of — so the line appears only when it
 * carries information. An absent denominator also prints nothing: a backend that does not
 * report coverage cannot be quoted as evidence of full coverage.
 */
function pageCoverage(
  counted: number | null | undefined,
  transmitted: number | null | undefined,
): string | null {
  if (typeof counted !== 'number' || !Number.isFinite(counted)) return null
  if (typeof transmitted !== 'number' || !Number.isFinite(transmitted)) return null
  if (counted === transmitted) return null
  return `${fmtInt(counted)} of ${fmtInt(transmitted)} runs counted`
}

/**
 * Page-delta sub-line for a RECEIVED page counter, or `null` for "print nothing".
 *
 * The legs should agree; a gap between them is page loss, and surfacing it is the entire
 * reason both legs are counted instead of just the one. Shown only when both legs are
 * present AND differ — equal legs are the expected case and need no commentary, and one
 * missing leg makes the subtraction meaningless rather than zero, which is why this
 * returns null instead of treating an absent counter as 0.
 */
function pageDelta(
  sent: number | null | undefined,
  received: number | null | undefined,
): string | null {
  if (typeof sent !== 'number' || !Number.isFinite(sent)) return null
  if (typeof received !== 'number' || !Number.isFinite(received)) return null
  if (sent === received) return null
  const gap = sent - received
  return gap > 0 ? `${fmtInt(gap)} fewer than sent` : `${fmtInt(-gap)} more than sent`
}

function fmtWhen(iso: string | null | undefined): string | null {
  if (!iso) return null
  const when = new Date(iso)
  // Null, not an em dash: callers use null to hide the surrounding text entirely.
  // The formatting itself goes through the shared helper so the zone and its
  // abbreviation are explicit rather than whatever the browser's default locale
  // happens to render.
  return Number.isFinite(when.getTime()) ? fmtDateTime(iso) : null
}

function successColour(pct: number | null | undefined): string | undefined {
  if (typeof pct !== 'number' || !Number.isFinite(pct)) return undefined
  return pct >= 95 ? '#4caf50' : pct >= 80 ? '#ff9800' : '#f44336'
}

function failureColour(pct: number | null | undefined): string | undefined {
  if (typeof pct !== 'number' || !Number.isFinite(pct)) return undefined
  return pct <= 5 ? '#4caf50' : pct <= 20 ? '#ff9800' : '#f44336'
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [range, setRange] = useState<TimeRange>({ mode: 'relative', hours: 24 })
  const [loading, setLoading] = useState(true)
  /**
   * SLA drill-through target. `null` is "no drill", and the three legs are mutually
   * exclusive because they are three different questions — a run can breach outbound while
   * meeting inbound, so OR-ing them would answer neither.
   */
  const [slaDrill, setSlaDrill] = useState<'combined' | 'outbound' | 'inbound' | null>(null)

  const win = useMemo(() => toWindow(range), [range])
  // Effects key on the resolved window's VALUE, not the object identity `useMemo`
  // hands back, so a re-render never re-triggers a fetch on its own.
  const key = windowKey(win)

  useEffect(() => {
    setLoading(true)
    api.dashboardStats(win).then(setStats).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      api.dashboardStats(win).then(setStats)
    }, 30000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (loading) return <div>Loading dashboard...</div>
  if (!stats) return <div>Failed to load dashboard stats</div>

  const windowFrom = fmtWhen(stats.window_from)
  const windowTo = fmtWhen(stats.window_to)
  const outbound = stats.active_outbound_sla_breaches
  const inbound = stats.active_inbound_sla_breaches

  /**
   * SLA breach count for the SELECTED WINDOW — the number the tile shows and the number
   * its drill-through returns, because both come from the same `sla_breached IS TRUE`
   * predicate over the same window on the same column.
   *
   * `active_sla_breaches` is the fallback for a backend that predates the windowed keys.
   * The two answer different questions and the caption says which is which: a run that
   * breached an hour ago and has since settled is IN the window count and is NOT active.
   */
  const breachesWindow = stats.sla_breaches_window ?? stats.active_sla_breaches
  const outboundWindow = stats.outbound_sla_breaches_window ?? outbound
  const inboundWindow = stats.inbound_sla_breaches_window ?? inbound
  const perLeg =
    typeof outbound === 'number' || typeof inbound === 'number'
      ? `outbound ${fmtInt(outbound)} · inbound ${fmtInt(inbound)} active now`
      : 'per-leg split not reported by this backend'

  /** The window the BACKEND resolved. Single source of truth for both consumers. */
  const resolvedWindow = { from: stats.window_from, to: stats.window_to }

  return (
    <div>
      {/* NO PAGE HEADING (FINISH-LINE A1.1). The Settings/nav highlight already says
          which page this is, so an <h2>Dashboard</h2> was one line of vertical space
          restating it. `flex-end` rather than `space-between` because the picker is now
          the row's only child and space-between would slide it to the left margin,
          away from the controls it belongs with. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '0.5rem', gap: '1rem', flexWrap: 'wrap' }}>
        <TimeRangePicker value={range} onChange={setRange} />
      </div>

      {/* The RESOLVED window, as the backend computed it — the only honest label for
          numbers produced from an absolute range or a rounded relative one. */}
      <div style={{ fontSize: '0.8rem', color: '#9aa0a6', marginBottom: '1.5rem' }}>
        {windowFrom && windowTo
          ? `Window ${windowFrom} → ${windowTo}${typeof stats.period_hours === 'number' ? ` (${stats.period_hours}h)` : ''}`
          : `Window: last ${fmtInt(stats.period_hours)}h`}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <StatCard
          label="Success Rate"
          value={fmtPercent(stats.success_rate_percent)}
          sub={overSettled(stats.successful_runs, stats.terminal_runs)}
          color={successColour(stats.success_rate_percent)}
        />
        <StatCard
          label="Failure Rate"
          value={fmtPercent(stats.failure_rate_percent)}
          sub={overSettled(stats.failed_runs, stats.terminal_runs)}
          color={failureColour(stats.failure_rate_percent)}
        />
        <StatCard
          label="In-Flight"
          value={fmtInt(stats.in_flight_count)}
          sub={`${fmtInt(stats.total_runs)} runs started in window`}
        />
        <StatCard
          label="Avg Combined E2E"
          value={fmtSeconds(stats.avg_e2e_seconds, 1)}
          sub={overSettled(stats.successful_runs, stats.terminal_runs)}
        />
        <StatCard
          label="Avg sec/page"
          value={fmtSeconds(stats.avg_seconds_per_page, 2)}
        />
        {/* The page counters are read as PAIRS: sent beside received, window pair then
            all-time pair. A single leg cannot show page loss — only the disagreement
            between them can, which is why the received counters exist at all. */}
        <StatCard
          label="Pages Sent (window)"
          value={fmtInt(stats.pages_sent_window)}
          sub={pageCoverage(stats.runs_with_sent_pages_window, stats.runs_transmitted_window) ?? undefined}
        />
        <StatCard
          label="Pages Received (window)"
          value={fmtInt(stats.pages_received_window)}
          /* THE COVERAGE CAPTION NOW APPEARS HERE TOO, and that is the fix rather than a
             cosmetic change. It used to render on the SENT tile only, so the tile whose
             number looks impossible — received, larger than sent — was the one carrying no
             explanation. An operator read two bare numbers with the second bigger and had
             nowhere to learn that the smaller one is summed over fewer rows.

             When coverage IS complete the caption is silent and the delta speaks instead,
             because then the two legs describe the same population and subtraction is a
             measurement. Exactly one of the two prints, and neither is ever a guess. */
          sub={
            pageCoverage(stats.runs_with_sent_pages_window, stats.runs_transmitted_window) ??
            pageDelta(stats.pages_sent_window, stats.pages_received_window) ??
            undefined
          }
        />
        {/* PAGES DROPPED — the measurement both counters exist to make, finally printed.

            The window's sent and received totals agreed to within 3 pages over the paired
            population while three pages had genuinely been sent and never arrived, and
            nothing anywhere reported them. Two reasons they were invisible: a few pages
            cannot move visibly inside a total of thousands, and a row that lost 2 pages
            nets against a row that arrived with 2 extra (a provider cover sheet, a
            re-transmit), so an aggregate difference is not the same question as "did any
            fax lose pages".

            Counted PER ROW over paired rows only, and shown even at zero: "no pages lost
            over 402 paired runs" is a positive statement about the platform, which is what
            this tool is for. It falls silent only when there is no paired population to
            count over, because 0 of 0 would read as a clean bill of health nobody earned. */}
        {typeof stats.paired_page_rows_window === 'number' &&
        stats.paired_page_rows_window > 0 ? (
          <StatCard
            label="Pages dropped (window)"
            value={fmtInt(stats.pages_lost_window ?? 0)}
            sub={
              (stats.pages_lost_window ?? 0) === 0
                ? `none over ${fmtInt(stats.paired_page_rows_window)} paired runs`
                : `across ${fmtInt(stats.runs_losing_pages_window)} of ` +
                  `${fmtInt(stats.paired_page_rows_window)} paired runs`
            }
          />
        ) : null}
        {/* The all-time counters are NOT complete history — sent_page_count was only
            written from 4d36c42 onward and there was no backfill, while
            received_page_count has been written since long before it. So all-time
            received legitimately exceeds all-time sent by the whole pre-4d36c42 era, and
            NO delta is shown here: there is no all-time coverage pair that could tell a
            real page loss apart from that recording gap. The "counted since" caption was
            removed at the user's request; pages_counted_since is still in the API
            response for anyone reading the numbers analytically. */}
        <StatCard
          label="Pages Sent (all time)"
          value={fmtInt(stats.pages_sent_all_time)}
        />
        <StatCard
          label="Pages Received (all time)"
          value={fmtInt(stats.pages_received_all_time)}
        />
        <StatCard label="Active Jobs" value={fmtInt(stats.active_jobs)} />
        <StatCard
          label="SLA Breaches"
          value={fmtInt(breachesWindow)}
          sub={`in window · ${perLeg}`}
          color={breachesWindow > 0 ? '#f44336' : '#4caf50'}
          onClick={() => setSlaDrill(prev => (prev === 'combined' ? null : 'combined'))}
          active={slaDrill === 'combined'}
          hint="Show only runs that breached SLA in this window"
        />
      </div>

      {/* Per-leg narrowing. Offered because the two legs have DIFFERENT causes — an
          outbound breach is the platform accepting slowly, an inbound one is the fax not
          coming back — so "which direction" is the first question after "how many", and the
          run list has accepted independent per-leg filters since R5.1. Kept as secondary
          controls rather than tiles of their own: the combined count is what an operator
          glances at, the legs are what they click into. */}
      {(typeof outboundWindow === 'number' || typeof inboundWindow === 'number') && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', color: '#9aa0a6' }}>Narrow breaches to one leg:</span>
          <LegButton
            label={`Outbound ${fmtInt(outboundWindow)}`}
            active={slaDrill === 'outbound'}
            onClick={() => setSlaDrill(prev => (prev === 'outbound' ? null : 'outbound'))}
          />
          <LegButton
            label={`Inbound ${fmtInt(inboundWindow)}`}
            active={slaDrill === 'inbound'}
            onClick={() => setSlaDrill(prev => (prev === 'inbound' ? null : 'inbound'))}
          />
          {slaDrill && (
            <button onClick={() => setSlaDrill(null)} style={{ ...legBase, color: '#9aa0a6' }}>
              Clear breach filter
            </button>
          )}
        </div>
      )}

      {/* Full run listing below the stats. It is given the window the BACKEND resolved for
          the metrics above — not the picker state, and not a second resolution of "last 24
          hours" — so the tiles and the rows describe one population by construction. */}
      <GlobalRunHistory
        embedded
        window={resolvedWindow}
        slaFilter={slaDrill === 'combined' ? ['breach'] : []}
        outboundSlaFilter={slaDrill === 'outbound' ? ['breach'] : []}
        inboundSlaFilter={slaDrill === 'inbound' ? ['breach'] : []}
      />
    </div>
  )
}

const legBase: React.CSSProperties = {
  padding: '0.25rem 0.6rem',
  fontSize: '0.78rem',
  background: '#1e1e1e',
  border: '1px solid #333',
  borderRadius: 4,
  cursor: 'pointer',
  color: '#f5f5f5',
}

function LegButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      style={{
        ...legBase,
        borderColor: active ? '#f44336' : '#333',
        color: active ? '#f44336' : '#f5f5f5',
      }}
    >
      {label}
    </button>
  )
}

function StatCard({
  label,
  value,
  sub,
  color,
  onClick,
  active,
  hint,
}: {
  label: string
  value: string | number
  sub?: string
  color?: string
  onClick?: () => void
  active?: boolean
  hint?: string
}) {
  const interactive = Boolean(onClick)
  return (
    <div
      // A clickable tile is a real control, so it gets a real one: button role, keyboard
      // focus and Enter/Space activation. A bare onClick on a div is unreachable without a
      // mouse and this project's changes have to be accessibility compliant.
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? Boolean(active) : undefined}
      title={hint}
      onClick={onClick}
      onKeyDown={
        interactive
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      style={{
        background: '#1e1e1e',
        borderRadius: '8px',
        padding: '1.2rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
        border: `1px solid ${active ? '#f44336' : '#333'}`,
        cursor: interactive ? 'pointer' : undefined,
      }}
    >
      <div style={{ fontSize: '0.8rem', color: '#9aa0a6', marginBottom: '0.5rem' }}>{label}</div>
      <div style={{ fontSize: '1.8rem', fontWeight: 700, color: color || '#f5f5f5' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: '#7a8085', marginTop: '0.4rem', lineHeight: 1.3 }}>{sub}</div>}
    </div>
  )
}
